import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  Assignment as AssignmentIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  AccountTree as AccountTreeIcon,
} from "@material-ui/icons";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { tiposApi } from "src/services/tiposApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { obtenerMeses } from "src/utils/formatTime";
import { CATEGORIAS } from "src/constants";
import { BRAND } from "src/theme/brandTokens";

// Array vacio estable: evita que el default `= []` inline en RTK Query genere
// una referencia nueva en cada render.
const EMPTY_ARRAY = [];

// ─── Constantes ──────────────────────────────────────────────────────────────

const MESES = obtenerMeses();

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    backgroundColor: BRAND.color.primary,
    color: "#fff",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  closeButton: {
    color: "#fff",
    padding: theme.spacing(0.5),
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  sectionCard: {
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${BRAND.color.primary}`,
  },
  sectionIcon: {
    color: BRAND.color.primary,
    fontSize: "1.1rem",
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  aiCard: {
    background: "linear-gradient(135deg, #fff8ef 0%, #fff3e0 100%)",
    border: `1px solid ${BRAND.color.primary}30`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 1px 4px rgba(255,149,0,0.1)",
  },
  aiHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  aiTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  aiHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: 2,
  },
  aiButton: {
    background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  inactiveChip: {
    backgroundColor: "#fafafa",
    color: "#9e9e9e",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    height: 40,
    cursor: "pointer",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
  },
  cancelButton: {
    textTransform: "none",
    fontWeight: 500,
  },
  saveButton: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 130,
  },
}));

// ─── Validación ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  nombrePlan: Yup.string().required("Nombre del plan requerido"),
  valorPlan: Yup.number()
    .typeError("Debe ser un número")
    .min(0, "El valor no puede ser negativo")
    .required("Valor requerido"),
  tipoMoneda: Yup.string().required("Selecciona el tipo de moneda"),
  descripcion: Yup.string().required("Descripción interna requerida"),
});

// ─── Subcomponente: encabezado de sección ─────────────────────────────────────

function SectionHeader({ icon: Icon, title, classes }) {
  return (
    <Box className={classes.sectionHeader}>
      <Icon className={classes.sectionIcon} />
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {title}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function AgregarPlan({ open, setOpen, monedas }) {
  const classes = useStyles();
  const [generando, setGenerando] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [agregarPlan] = tiposApi.useAddTipoPlanMutation();
  const [generarDescripcion] = tiposApi.useGenerarDescripcionServicioMutation();

  // Fuentes de datos para la derivacion por defecto.
  const { data: todasSucursales = EMPTY_ARRAY } =
    sucursalesApi.useGetSucursalesQuery();
  const [triggerEmpleados, { data: empleados = EMPTY_ARRAY }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  const formik = useFormik({
    initialValues: {
      nombrePlan: "",
      valorPlan: 0,
      tipoMoneda: "",
      descripcion: "",
      categoria: "",
      descripcionPublica: "",
      alcance: "",
      condiciones: "",
      tipoFrecuenciaDefault: "",
      mesCargaTareasDefault: "",
      mesFacturacionDefault: "",
      sucursalDefault: "",
      empleadoEncargadoDefault: "",
      supervisorDefault: "",
      bloquearAsignacion: false,
      activo: true,
      orden: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await AlertaModalV2({
        title: "¿Agregar este plan?",
        text: `Se creará el plan "${values.nombrePlan.toUpperCase()}".`,
        confirmButtonText: "Sí, agregar",
        icon: "question",
      }).fire();

      if (!result.isConfirmed) return;

      let valorFinal = values.valorPlan;
      if (String(valorFinal).includes(",")) {
        valorFinal = parseFloat(String(valorFinal).replaceAll(",", "."));
      }

      try {
        await agregarPlan({
          nombrePlan: values.nombrePlan.toUpperCase(),
          valorPlan: valorFinal,
          tipoMoneda: values.tipoMoneda,
          descripcion: values.descripcion,
          categoria: values.categoria || undefined,
          descripcionPublica: values.descripcionPublica || undefined,
          alcance: values.alcance || undefined,
          condiciones: values.condiciones || undefined,
          tipoFrecuenciaDefault: values.tipoFrecuenciaDefault || undefined,
          mesCargaTareasDefault:
            values.tipoFrecuenciaDefault === "AÑO"
              ? values.mesCargaTareasDefault || undefined
              : undefined,
          mesFacturacionDefault:
            values.tipoFrecuenciaDefault === "AÑO"
              ? values.mesFacturacionDefault || undefined
              : undefined,
          sucursalDefault: values.sucursalDefault || undefined,
          empleadoEncargadoDefault:
            values.empleadoEncargadoDefault || undefined,
          supervisorDefault: values.supervisorDefault || undefined,
          bloquearAsignacion: values.bloquearAsignacion ? 1 : 0,
          activo: values.activo ? 1 : 0,
          orden: values.orden,
        }).unwrap();

        enqueueSnackbar("Plan agregado correctamente", { variant: "success" });
        handleCerrar();
      } catch {
        enqueueSnackbar("Error al agregar el plan. Intenta nuevamente.", {
          variant: "error",
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
    isSubmitting,
  } = formik;

  // Carga los empleados de la sucursal elegida para encargado/supervisor.
  useEffect(() => {
    if (values.sucursalDefault) {
      triggerEmpleados({ id: values.sucursalDefault, estado: "ALL" });
    }
  }, [values.sucursalDefault, triggerEmpleados]);

  const handleCerrar = () => {
    resetForm();
    setOpen(false);
  };

  const empleadoLabel = (emp) =>
    emp ? `${emp.nombre ?? ""} ${emp.apellidos ?? ""}`.trim() : "";

  const empleadoEncargadoSel =
    empleados.find((e) => e.rut === values.empleadoEncargadoDefault) ?? null;
  const supervisorSel =
    empleados.find((e) => e.rut === values.supervisorDefault) ?? null;

  const handleGenerarConIA = async () => {
    if (!values.nombrePlan.trim()) {
      enqueueSnackbar("Ingresa el nombre del plan antes de generar con IA", {
        variant: "warning",
      });
      return;
    }
    setGenerando(true);
    try {
      const resultado = await generarDescripcion({
        nombre: values.nombrePlan,
        descripcionInterna: values.descripcion || undefined,
        tipoServicio: "PLAN",
      }).unwrap();
      setFieldValue("descripcionPublica", resultado.descripcionPublica);
      setFieldValue("alcance", resultado.alcance);
      setFieldValue("condiciones", resultado.entregable);
      enqueueSnackbar("Descripción generada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al generar la descripción con IA", {
        variant: "error",
      });
    } finally {
      setGenerando(false);
    }
  };

  const esAnual = values.tipoFrecuenciaDefault === "AÑO";

  return (
    // disableEnforceFocus: evita el conflicto de aria-hidden entre MUI Dialog y SweetAlert2.
    // SweetAlert2 pone aria-hidden en el Dialog al abrirse; sin esta prop, MUI bloquea
    // el foco y lanza la advertencia en consola.
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      aria-labelledby="agregar-plan-title"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle className={classes.dialogTitle} id="agregar-plan-title">
        <Box className={classes.titleContent}>
          <AssignmentIcon />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Nuevo Plan de Servicio
          </Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleCerrar}
          size="small"
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogContent}>
            {/* ── Sección 1: Datos del plan ─────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AssignmentIcon}
                  title="Datos del plan"
                  classes={classes}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nombre del plan *"
                      fullWidth
                      autoFocus
                      variant="outlined"
                      size="small"
                      {...getFieldProps("nombrePlan")}
                      error={Boolean(touched.nombrePlan && errors.nombrePlan)}
                      helperText={
                        (touched.nombrePlan && errors.nombrePlan) ||
                        "Se guardará en mayúsculas"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Valor *"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      {...getFieldProps("valorPlan")}
                      error={Boolean(touched.valorPlan && errors.valorPlan)}
                      helperText={touched.valorPlan && errors.valorPlan}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(touched.tipoMoneda && errors.tipoMoneda)}
                    >
                      <InputLabel>Moneda *</InputLabel>
                      <Select {...getFieldProps("tipoMoneda")} label="Moneda *">
                        <MenuItem value="">
                          <em>Seleccionar</em>
                        </MenuItem>
                        {monedas.map((m, i) => (
                          <MenuItem key={i} value={m.nombre}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.tipoMoneda && errors.tipoMoneda && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginLeft: 14, marginTop: 3 }}
                        >
                          {errors.tipoMoneda}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Descripción interna *"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      {...getFieldProps("descripcion")}
                      error={Boolean(touched.descripcion && errors.descripcion)}
                      helperText={
                        (touched.descripcion && errors.descripcion) ||
                        "Visible solo para el equipo interno"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Categoría</InputLabel>
                      <Select {...getFieldProps("categoria")} label="Categoría">
                        <MenuItem value="">
                          <em>Sin categoría</em>
                        </MenuItem>
                        {CATEGORIAS.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2: Configuración ──────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={SettingsIcon}
                  title="Configuración"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Frecuencia por defecto</InputLabel>
                      <Select
                        {...getFieldProps("tipoFrecuenciaDefault")}
                        label="Frecuencia por defecto"
                      >
                        <MenuItem value="">
                          <em>Sin definir</em>
                        </MenuItem>
                        <MenuItem value="MES">Mensual</MenuItem>
                        <MenuItem value="AÑO">Anual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {esAnual && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Mes carga de tareas</InputLabel>
                          <Select
                            {...getFieldProps("mesCargaTareasDefault")}
                            label="Mes carga de tareas"
                          >
                            <MenuItem value="">
                              <em>Sin definir</em>
                            </MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m} value={m}>
                                {m}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Mes facturación</InputLabel>
                          <Select
                            {...getFieldProps("mesFacturacionDefault")}
                            label="Mes facturación"
                          >
                            <MenuItem value="">
                              <em>Sin definir</em>
                            </MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m} value={m}>
                                {m}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={esAnual ? 6 : 4}>
                    <TextField
                      label="Orden de visualización"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Menor número = aparece primero"
                      {...getFieldProps("orden")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={esAnual ? 6 : 4}>
                    <Box className={classes.statusBox}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.activo}
                            onChange={(e) =>
                              setFieldValue("activo", e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Chip
                            label={values.activo ? "Activo" : "Inactivo"}
                            size="small"
                            className={
                              values.activo
                                ? classes.activeChip
                                : classes.inactiveChip
                            }
                          />
                        }
                        style={{ margin: 0 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2b: Derivación por defecto ────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AccountTreeIcon}
                  title="Derivación por defecto (opcional)"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={todasSucursales}
                      getOptionLabel={(option) => option.nombre ?? option}
                      isOptionEqualToValue={(option, value) =>
                        (option.nombre ?? option) === (value.nombre ?? value)
                      }
                      value={
                        todasSucursales.find(
                          (s) => s.nombre === values.sucursalDefault,
                        ) ?? null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue(
                          "sucursalDefault",
                          newValue ? newValue.nombre : "",
                        );
                        // Reinicia encargado/supervisor al cambiar de sucursal.
                        setFieldValue("empleadoEncargadoDefault", "");
                        setFieldValue("supervisorDefault", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Sucursal por defecto"
                          placeholder="Seleccionar sucursal..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={empleadoEncargadoSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "empleadoEncargadoDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Encargado por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar encargado..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={supervisorSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "supervisorDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Supervisor por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar supervisor..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.bloquearAsignacion}
                          onChange={(e) =>
                            setFieldValue(
                              "bloquearAsignacion",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label="Bloquear asignación en la propuesta"
                      style={{ margin: 0 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3: Información pública + IA ──────────────────────── */}
            <Card className={classes.aiCard}>
              <CardContent>
                <Box className={classes.aiHeader}>
                  <Box className={classes.aiTitleGroup}>
                    <PublicIcon className={classes.sectionIcon} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        className={classes.sectionTitle}
                      >
                        Información pública del servicio
                      </Typography>
                      <Typography className={classes.aiHint}>
                        Visible para el cliente en propuestas comerciales
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip
                    title={
                      !values.nombrePlan.trim()
                        ? "Ingresa el nombre del plan para habilitar"
                        : "Genera descripción, alcance y condiciones automáticamente con IA"
                    }
                    placement="left"
                  >
                    <span>
                      <Button
                        className={classes.aiButton}
                        onClick={handleGenerarConIA}
                        disabled={generando || !values.nombrePlan.trim()}
                        startIcon={
                          generando ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <AutoFixHighIcon />
                          )
                        }
                        size="small"
                      >
                        {generando ? "Generando..." : "Generar con IA"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción pública"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Describe el servicio en términos que el cliente pueda entender..."
                      helperText={`${(values.descripcionPublica || "").length} caracteres`}
                      {...getFieldProps("descripcionPublica")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Alcance"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué incluye este servicio?"
                      {...getFieldProps("alcance")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Condiciones"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Condiciones de entrega o consideraciones importantes..."
                      {...getFieldProps("condiciones")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>

          {/* ── Footer ──────────────────────────────────────────────────────── */}
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={handleCerrar}
              variant="outlined"
              className={classes.cancelButton}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveButton}
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={16} color="inherit" />
              }
            >
              {isSubmitting ? "Guardando..." : "Agregar Plan"}
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
